/**
 * Язык интерфейса: выбор ученика, сохраняется между запусками.
 *
 * Без выбора — язык системы, если интерфейс на нём есть, иначе русский.
 * Смена языка сразу переключает i18next (`i18n/index.ts` подписан на стор)
 * и атрибут `lang` документа — от него зависят переносы и озвучка.
 */

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { DEFAULT_UI_LANGUAGE, isUiLanguage, toLanguageCode, UI_LANGUAGES, type UiLanguageCode } from '@aptile/shared';

interface LocaleState {
  readonly uiLanguage: UiLanguageCode;
  readonly setUiLanguage: (language: UiLanguageCode) => void;
}

function systemLanguage(): UiLanguageCode {
  const candidates = typeof navigator === 'undefined' ? [] : [...(navigator.languages ?? []), navigator.language];
  for (const candidate of candidates) {
    const code = toLanguageCode(candidate);
    // Язык системы, на котором интерфейса нет (испанский), пропускается.
    if (code !== null && isUiLanguage(code)) return code;
  }
  return isUiLanguage(DEFAULT_UI_LANGUAGE) ? DEFAULT_UI_LANGUAGE : 'ru';
}

export const useLocaleStore = create<LocaleState>()(
  persist(
    (set) => ({
      uiLanguage: systemLanguage(),
      setUiLanguage: (uiLanguage) => set({ uiLanguage }),
    }),
    {
      name: 'aptile-locale',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ uiLanguage: state.uiLanguage }),
      // Повреждённое или устаревшее значение — язык системы, а не падение.
      merge: (persisted, current) => {
        const stored = (persisted as { readonly uiLanguage?: unknown } | undefined)?.uiLanguage;
        const uiLanguage = UI_LANGUAGES.find((code) => code === stored) ?? current.uiLanguage;
        return { ...current, uiLanguage };
      },
    },
  ),
);
